import React, { useState, useCallback, useEffect, useRef } from "react";
import Auth, { isAuthenticated, getUser, clearAuth } from "./pages/Auth";
import PatientSearch from "./components/PatientSearch";
import PatientDetail from "./components/PatientDetail";
import PatientComparison from "./components/PatientComparison";
import DashboardHome from "./components/DashboardHome";
import AnalyticsPage from "./components/AnalyticsPage";
import AdminPanel from "./components/AdminPanel";
import { PrivacyPolicy, SecurityPage, InsightsPage } from "./components/InfoPages";
import InitiationPhase from "./components/InitiationPhase";
import MaintenancePhase from "./components/MaintenancePhase";
import DiscontinuationPhase from "./components/DiscontinuationPhase";
import CustomerSwitcher from "./components/CustomerSwitcher";
import RoleGuard, { getVisibleTabs } from "./components/RoleGuard";
import EmployerDashboard from "./components/EmployerDashboard";

const SESSION_MAX_MS = 8 * 60 * 60 * 1000;

function TabButton({ tab, isActive, onClick }) {
  const [showTip, setShowTip] = useState(false);
  const timerRef = useRef(null);

  const handleEnter = useCallback(() => {
    timerRef.current = setTimeout(() => setShowTip(true), 400);
  }, []);
  const handleLeave = useCallback(() => {
    clearTimeout(timerRef.current);
    setShowTip(false);
  }, []);

  return (
    <div style={{ position: "relative" }} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button onClick={onClick} style={{
        ...s.tabBtn,
        color: isActive ? "#00e5ff" : "#4a6175",
        borderBottom: isActive ? "2px solid #00e5ff" : "2px solid transparent",
        display: "flex", alignItems: "center",
      }}>
        <span style={{ marginRight: 4 }}>{tab.icon}</span>
        {tab.label}
        {tab.description && (
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 14, height: 14, borderRadius: "50%", marginLeft: 5,
            fontSize: 9, fontWeight: 700, color: "#4a6175",
            border: "1px solid #2a3a4a", lineHeight: 1,
          }}>i</span>
        )}
      </button>
      {showTip && tab.description && (
        <div style={{
          position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
          marginTop: 6, padding: "10px 14px", borderRadius: 8, fontSize: 11, lineHeight: 1.5,
          color: "#e6edf3", background: "rgba(13,17,23,0.97)", border: "1px solid #1e2d3d",
          boxShadow: "0 8px 24px rgba(0,0,0,0.5)", width: 260, zIndex: 200,
          pointerEvents: "none", whiteSpace: "normal",
        }}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4, color: "#00e5ff" }}>{tab.label}</div>
          {tab.description}
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [comparePatients, setComparePatients] = useState([]);

  useEffect(() => { if(isAuthenticated())setUser(getUser()); setReady(true); }, []);

  useEffect(() => {
    if(!user)return;
    const exp=parseInt(sessionStorage.getItem("pathriva_token_exp")||"0",10);
    const remaining=exp-Date.now();
    if(remaining<=0){handleLogout();return;}
    const timer=setTimeout(handleLogout,Math.min(remaining,SESSION_MAX_MS));
    return()=>clearTimeout(timer);
  }, [user]); // eslint-disable-line

  const handleLogin=useCallback((u)=>setUser(u),[]);
  const handleLogout=useCallback(()=>{clearAuth();setUser(null);setActiveTab("dashboard");},[]);

  if(!ready)return null;
  if(!user)return <Auth onLogin={handleLogin}/>;

  const tabs=getVisibleTabs(user.role);

  return (
    <div style={s.root}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg);}}
        @media(max-width:768px){
          .ph-topbar{flex-wrap:wrap;gap:8px;padding:8px 12px!important}
          .ph-tabs{order:3;width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch}
          .ph-tabs::-webkit-scrollbar{display:none}
          .ph-content{padding:12px 14px!important}
          .ph-title{font-size:16px!important}
          .ph-user-info{display:none!important}
          table th:nth-child(n+4),table td:nth-child(n+4){display:none}
          .ph-detail-panel{width:100%!important;max-width:100%!important;border-left:none!important;border-radius:0!important}
        }
      `}</style>

      <div style={s.topBar} className="ph-topbar">
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={s.logo}>P</div>
          <div><div style={{fontWeight:700,fontSize:13}}>Pathriva</div>
            <div style={{fontSize:9,color:"#4a6175",letterSpacing:2}}>CLINICAL OUTCOMES ANALYTICS</div></div>
        </div>
        <div style={{display:"flex",gap:2}} className="ph-tabs">
          {tabs.map((tab)=>(
            <TabButton key={tab.id} tab={tab} isActive={activeTab===tab.id} onClick={()=>setActiveTab(tab.id)} />
          ))}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <div style={{textAlign:"right"}} className="ph-user-info">
            <div style={{fontSize:11,color:"#e6edf3"}}>{user.email}</div>
            <div style={{fontSize:9,color:"#4a6175",textTransform:"uppercase",letterSpacing:1}}>{user.role}</div>
          </div>
          <CustomerSwitcher />
          <button onClick={handleLogout} style={s.logoutBtn}>Sign out</button>
        </div>
      </div>

      <div style={s.content} className="ph-content">
        {activeTab==="dashboard"&&<div><div style={s.pageTitle}>Dashboard</div>
          <RoleGuard user={user} allow={["super_admin","admin","operator"]}><DashboardHome/></RoleGuard>
          <RoleGuard user={user} allow={["employer"]}><EmployerDashboard/></RoleGuard>
          <RoleGuard user={user} allow={["viewer"]}><div style={s.viewerMsg}>Contact your administrator for detailed analytics access.</div></RoleGuard>
        </div>}

        {activeTab==="initiation"&&<RoleGuard user={user} allow={["super_admin","admin","operator"]}>
          <div><div style={s.pageTitle}>Initiation Phase</div><InitiationPhase/></div></RoleGuard>}

        {activeTab==="maintenance"&&<RoleGuard user={user} allow={["super_admin","admin","operator"]}>
          <div><div style={s.pageTitle}>Maintenance Phase</div><MaintenancePhase/></div></RoleGuard>}

        {activeTab==="discontinuation"&&<RoleGuard user={user} allow={["super_admin","admin","operator"]}>
          <div><div style={s.pageTitle}>Discontinuation Phase</div><DiscontinuationPhase/></div></RoleGuard>}

        {activeTab==="patients"&&<div><div style={s.pageTitle}>Patients</div>
          <PatientSearch onSelectPatient={(p)=>setSelectedPatient(p)}
            onComparePatient={(p)=>{
              setComparePatients(prev=>{
                if(prev.find(x=>x.patient_id===p.patient_id))return prev;
                if(prev.length>=2)return[prev[1],p];
                return[...prev,p];
              });
            }}
            comparePatients={comparePatients}/></div>}
        {comparePatients.length===2&&<PatientComparison patientA={comparePatients[0]} patientB={comparePatients[1]} onClose={()=>setComparePatients([])}/>}

        {activeTab==="analytics"&&<RoleGuard user={user} allow={["super_admin","admin","operator"]}>
          <div><div style={s.pageTitle}>Analytics</div><AnalyticsPage/></div></RoleGuard>}

        {activeTab==="admin"&&<RoleGuard user={user} allow={["super_admin","admin"]}>
          <div><div style={s.pageTitle}>Admin Panel</div><AdminPanel/></div></RoleGuard>}

        {activeTab==="insights"&&<InsightsPage/>}
        {activeTab==="privacy"&&<PrivacyPolicy/>}
        {activeTab==="security"&&<SecurityPage/>}
      </div>

      {/* Footer */}
      <div style={{padding:"16px 28px",borderTop:"1px solid #1e2d3d",display:"flex",justifyContent:"center",gap:20,fontSize:10,color:"#4a6175"}}>
        <span onClick={()=>setActiveTab("insights")} style={{cursor:"pointer"}}>Insights</span>
        <span onClick={()=>setActiveTab("privacy")} style={{cursor:"pointer"}}>Privacy</span>
        <span onClick={()=>setActiveTab("security")} style={{cursor:"pointer"}}>Security</span>
        <span>© 2026 Pathriva</span>
      </div>

      {selectedPatient&&<PatientDetail patient={selectedPatient} onClose={()=>setSelectedPatient(null)}/>}
    </div>
  );
}


const s = {
  root:{minHeight:"100vh",background:"#060810",color:"#e6edf3",fontFamily:"'Inter',-apple-system,sans-serif"},
  topBar:{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 20px",borderBottom:"1px solid #1e2d3d",background:"rgba(13,17,23,0.98)",position:"sticky",top:0,zIndex:100},
  logo:{width:28,height:28,background:"linear-gradient(135deg,#00e5ff,#7c3aed)",borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,color:"#000"},
  tabBtn:{background:"none",border:"none",padding:"8px 14px",fontSize:11,fontFamily:"inherit",cursor:"pointer",fontWeight:600,letterSpacing:0.5,transition:"all 0.15s"},
  logoutBtn:{padding:"6px 14px",background:"rgba(255,77,109,0.1)",border:"1px solid rgba(255,77,109,0.3)",borderRadius:6,color:"#ff4d6d",fontSize:10,cursor:"pointer",fontWeight:600,fontFamily:"inherit"},
  content:{padding:"24px 28px",maxWidth:1200,margin:"0 auto"},
  pageTitle:{fontSize:20,fontWeight:800,color:"#e6edf3",marginBottom:20},
  viewerMsg:{background:"#131920",border:"1px solid #1e2d3d",borderRadius:10,padding:24,color:"#4a6175",fontSize:13,textAlign:"center"},
};
